// Table/Odx-Table.tsx

import React, {ReactElement, useMemo, useState} from 'react';
import OdxPaginator from './Odx-Paginator';
import {orderBy} from "../../../Utils/sorting";

export interface TableHeaderCell {
    name: string;
    title?: string;
    sortable?: boolean;
    className?: string;
    required?: boolean;
}

interface OdxTableProps {
    children: ReactElement[] | ReactElement;
    headerData?: TableHeaderCell[];
    ariaDescribedby?: string;
    className?: string;
    variant?: "default" | "striped";
    paginated?: boolean;
    itemsPerPage?: number;
    tableHeight?: string;
    id?: string
}

/*
 * @param children - The children of the table, should be an array of OdxTableRow components
 * @param headerData - An array of objects that define the header cells of the table
 * @param ariaDescribedby - The ID of an element that describes the table
 * @param className - Additional classes to apply to the table
 * @param variant - The variant of the table, either "default" or "striped"
 * @param paginated - Whether the table should be paginated
 * @param itemsPerPage - The number of items per page if the table is paginated
 */

const OdxTable: React.FC<OdxTableProps> = ({
                                               children,
                                               headerData = [],
                                               ariaDescribedby = "",
                                               className,
                                               variant = "default",
                                               paginated = false,
                                               itemsPerPage = 10,
                                               tableHeight = "50",
                                               id
                                           }) => {
    const [sortColumn, setSortColumn] = useState<string | null>(null);
    const [sortAscending, setSortAscending] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);

    const handleSort = (columnName: string) => {
        const newSortAscending =
            sortColumn === columnName ? !sortAscending : true;
        setSortColumn(columnName);
        setSortAscending(newSortAscending);
    };

    const findContent = (child: React.ReactNode): string | null => {
        if (typeof child === 'number') {
            return child.toString();
        } else if (typeof child === 'string') {
            return child.trim();
        } else if (React.isValidElement(child)) {
            if (typeof child.props.content === 'string') {
                return child.props.content.trim();
            } else {
                return findContent(child.props.children);
            }
        } else if (Array.isArray(child)) {
            for (const subChild of child) {
                const content = findContent(subChild);
                if (content) return content;
            }
        }
        return null;
    };

    const sortedChildren = useMemo(() => {
        const columnIndex = headerData.findIndex(
            (header) => header.name === sortColumn
        );

        if (columnIndex === -1) {
            return React.Children.toArray(children);
        }

        function getValueFromChild(child: ReactElement) {
            const cell = React.Children.toArray(child.props.children)[columnIndex];
            return findContent(cell);
        }

        return orderBy(children, x => getValueFromChild(x), sortAscending);
    }, [children, sortColumn, sortAscending, headerData]);

    const totalPages = useMemo(() => {
        const totalItems = sortedChildren.length;

        const totalP = Math.max(Math.ceil(totalItems / itemsPerPage), 1);
        if (currentPage > totalP) {
            setCurrentPage(totalP);
        }
        return totalP;
    }, [sortedChildren, itemsPerPage]);

    const paginatedChildren = useMemo(() => {
        if (!paginated) {
            return sortedChildren;
        }
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return sortedChildren.slice(startIndex, endIndex);
    }, [sortedChildren, currentPage, itemsPerPage, paginated]);

    // Determine active columns based on paginated children
    const activeColumnIndices = useMemo(() => {
        if (headerData.length === 0) return [];

        // Initialize an array to track active columns
        const active = Array(headerData.length).fill(false);

        // Set Header Data to true where required
        headerData.forEach((header) => {
            if (header.required) {
                active[headerData.indexOf(header)] = true;
            }
        });

        // Convert paginatedChildren to an array for iteration
        const childrenArray = paginatedChildren;

        childrenArray.forEach((row) => {
            // Assuming each row's children are the cells
            const cells = React.Children.toArray(
                (row as ReactElement).props.children
            );
            cells.forEach((cell, index) => {
                const content = findContent(cell);
                if (content && content.trim() !== '' && !active[index]) {
                    active[index] = true;
                }
            });
        });
        // Get the indices of columns that have at least one non-empty cell
        return active.reduce<number[]>((acc, isActive, index) => {
            if (isActive) acc.push(index);
            return acc;
        }, []);
    }, [paginatedChildren, headerData]);

    // Filtered header data based on active columns
    const filteredHeaderData = useMemo(() => {
        const columns = activeColumnIndices.map((index) => headerData[index]);
        // filter out undefined columns
        if (columns.some((column) => !column)) {
            throw new Error('OdxTable: Header count != Table column count');
        }
        return columns //.filter((column) => column !== undefined);
    }, [activeColumnIndices, headerData]);

    // Adjust sorted and paginated children to only include active columns
    const filteredPaginatedChildren = useMemo(() => {
        if (activeColumnIndices.length === headerData.length) {
            // All columns are active; no need to filter
            return paginatedChildren;
        }

        return paginatedChildren.map((row) => {
            const cells = React.Children.toArray(
                (row as ReactElement).props.children
            );
            const filteredCells = activeColumnIndices.map((index) => cells[index]);
            return React.cloneElement(row as ReactElement, {
                children: filteredCells,
            });
        });
    }, [paginatedChildren, activeColumnIndices, headerData.length]);


    return (
        <div
            className={`odx-table-container ${className || ''} h-full`}

        >
            <div className={"h-full "}>
                <div className="h-full overflow-y-auto">
                    <table
                        className={`odx-table--${variant} odx-table w-full`}
                        role="table"
                        id={"OdxTable_" + id}
                        aria-describedby={ariaDescribedby}
                    >
                        {filteredHeaderData.length > 0 && (
                            <thead role="rowgroup" className="odx-table__head">
                            <tr role="row" className="odx-table__header-row">
                                {filteredHeaderData.map((headerCell, index) => (
                                    <th
                                        key={index}
                                        role="columnheader"
                                        className={`odx-table__header-cell ${
                                            headerCell.sortable ? 'sortable' : ''
                                        } ${headerCell.className || ''} sticky top-0 bg-white z-9`}
                                        onClick={() =>
                                            headerCell.sortable &&
                                            handleSort(headerCell.name)
                                        }
                                    >
                                        <div
                                            className={`odx-table__header-cell-title ${
                                                headerCell.sortable ? 'sortable' : ''
                                            } ${headerCell.className || ''}`}
                                        >
                                            {headerCell.title}
                                            {sortColumn === headerCell.name && (
                                                <span>{sortAscending ? ' ▲' : ' ▼'}</span>
                                            )}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                            </thead>
                        )}
                        <tbody role="rowgroup" className="odx-table__body">
                        {filteredPaginatedChildren}
                        </tbody>
                    </table>
                </div>

                {paginated && (
                    <>
                        <odx-separator style={{margin: '1rem 0'}} />
                        <div className="odx-table__paginator">
                            <OdxPaginator
                                currentPage={currentPage}
                                totalPages={totalPages}
                                itemCount={sortedChildren.length}
                                itemsPerPage={itemsPerPage}
                                onPageChange={setCurrentPage}
                            />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default OdxTable;
